import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { formatAddressLines } from '~~/layers/base/utils/formatAddressLine'
import {
    useAddressService,
    type ProfileAddressRecord,
} from '~~/layers/base/services/address.service'
import type { AddressPayload } from '~~/layers/base/stores/contact.store'

export interface ReturnAddressOption {
    id: string
    label?: string | null
    prefix?: string | null
    first_name?: string | null
    last_name?: string | null
    suffix?: string | null
    raw_address_str?: string | null
    street_address_1?: string | null
    street_address_2?: string | null
    city?: string | null
    state?: string | null
    zip_code?: string | null
    country?: string | null
    country_code?: string | null
    handwriting_stationery_id?: string | null
    /** Stationery-only row injected when not yet in profile list (picker custom section). */
    isCustomStationery?: boolean
    lines: string[]
    isDefault: boolean
}

export const useReturnAddressStore = defineStore('returnAddress', () => {
    const { doFetchAddressList, doCreateAddress, doPatchAddress } = useAddressService()
    const addresses = ref<ProfileAddressRecord[]>([])
    const loading = ref(false)
    /**
     * Profile make-default writes this store as well as `useProfileStore` so Send can
     * show the new default before PATCH returns. Personalize falls back to
     * `options.find(o => o.isDefault)` — it does not read the Account address cache.
     * Keep the optimistic id until commit/revert so a `fetchList` GET that still
     * sees the old server default does not overwrite the picker.
     */
    let pendingDefaultId: string | null = null

    function applyDefaultFlags(id: string | null): void {
        if (!id) {
            return
        }
        addresses.value = addresses.value.map((row) => ({
            ...row,
            is_default: row.id === id,
        }))
    }

    /** Address options for picker UIs (selection id + display lines + default hint). */
    const options = computed<ReturnAddressOption[]>(() =>
        addresses.value.map((address) => ({
            id: address.id,
            label: address.label,
            prefix: address.prefix,
            first_name: address.first_name,
            last_name: address.last_name,
            suffix: address.suffix,
            raw_address_str: address.raw_address_str,
            street_address_1: address.street_address_1,
            street_address_2: address.street_address_2,
            city: address.city,
            state: address.state,
            zip_code: address.zip_code,
            country: address.country,
            country_code: address.country_code,
            handwriting_stationery_id: address.handwriting_stationery_id,
            lines: formatAddressLines(address),
            isDefault: Boolean(address.is_default),
        }))
    )

    /**
     * Load all profile-owned return addresses (`GET /api/addresses`).
     * Re-applies `pendingDefaultId` after the response so Account make-default
     * still wins if this GET raced the PATCH.
     */
    async function fetchList() {
        loading.value = true
        try {
            const { data, error } = await doFetchAddressList()
            if (error.value) throw error.value
            addresses.value = data.value?.addresses ?? []
            applyDefaultFlags(pendingDefaultId)
            return addresses.value
        } catch {
            return addresses.value
        } finally {
            loading.value = false
        }
    }

    /** Create a new return address row, then refresh list for consistent defaults. */
    async function createAddress(body: AddressPayload & { is_default?: boolean }) {
        const { data, error } = await doCreateAddress(body)
        if (error.value) throw error.value
        await fetchList()
        return data.value?.address ?? null
    }

    /** Update one return address row, then refresh list for consistent defaults. */
    async function updateAddress(
        id: string,
        body: Partial<AddressPayload> & { is_default?: boolean }
    ) {
        const { data, error } = await doPatchAddress(id, body)
        if (error.value) throw error.value
        await fetchList()
        return data.value?.address ?? null
    }

    /** Find one loaded return address by id from local state. */
    function getAddressById(id: string | null | undefined) {
        if (!id) return null
        return addresses.value.find((row) => row.id === id) ?? null
    }

    /** Drop loaded return addresses (e.g. logout session wipe). */
    function clearAddresses(): void {
        pendingDefaultId = null
        addresses.value = []
        loading.value = false
    }

    /**
     * Account make-default: flip `isDefault` in this Send cache immediately
     * (and remember the id for a racing `fetchList`). Not Better Auth session.
     */
    function applyOptimisticDefault(id: string): void {
        pendingDefaultId = id
        applyDefaultFlags(id)
    }

    /** PATCH succeeded — later `fetchList` should trust the server default. */
    function commitOptimisticDefault(): void {
        pendingDefaultId = null
    }

    /** PATCH failed — restore the previous default and drop the pending id. */
    function revertOptimisticDefault(previousDefaultId: string | null): void {
        pendingDefaultId = null
        if (previousDefaultId) {
            applyDefaultFlags(previousDefaultId)
            return
        }
        addresses.value = addresses.value.map((row) => ({
            ...row,
            is_default: false,
        }))
    }

    return {
        addresses,
        options,
        loading,
        fetchList,
        createAddress,
        updateAddress,
        getAddressById,
        clearAddresses,
        applyOptimisticDefault,
        commitOptimisticDefault,
        revertOptimisticDefault,
    }
})
